import { Fragment, useState, useEffect, useRef } from 'react';
import { animated, useTransition } from 'react-spring';
import { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { formatDateTime, PageViewTracker, PATHS } from '@belong/common';
import { IconFb, IconLinkedin, IconTwitter } from '@belong/icons';
import {
  Image,
  Link,
  LinkBase,
  PrismicImageAdapter,
  Spinner,
  Text,
} from '@belong/ui';
import clsx from 'clsx';
import { type IntercomWidgetProps } from 'src/components/intercom-widget/intercom-widget';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { BLOG_STRINGS } from 'src/features/blog/blog.strings';
import { AboutAuthor } from 'src/features/blog/components/about-author/about-author';
import { BlogTags } from 'src/features/blog/components/blog-tags/blog-tags';
import { BlogCard } from 'src/features/blog/components/card/card';
import { ContentTable } from 'src/features/blog/components/content-table/content-table';
import type { ContentTableItem } from 'src/features/blog/components/content-table-items/content-table-items';
import { HomeownersWidget } from 'src/features/blog/components/homeowners-widget/homeowners-widget';
import { RequestFeature } from 'src/features/blog/components/request-feature/request-feature';
import { StickyNavbar } from 'src/features/blog/components/sticky-navbar/sticky-navbar';
import type { BlogPost as BlogPostDocument } from 'src/features/blog/types';
import {
  nextAndPrevButtonsContent,
  SliceType,
  SLICE_COMPONENT_MAP,
} from 'src/features/blog/constants';
import { filterHeadingsAndDescendants } from 'src/features/blog/helpers';
import css from 'src/features/blog/styles/blog-post.module.css';
import {
  fetchBlogPostByUid,
  fetchBlogPostsList,
  fetchNextBlogPost,
  fetchPreviousBlogPost,
  fetchBlogProfileByUid,
} from 'src/features/cms/prismic';
import { HOMEOWNERS_STRINGS } from 'src/features/homeowners/homeowners.strings';
import { useOnScreen } from 'src/features/homeowners/hooks/use-on-screen';
import { createClient } from 'src/prismicio';
import { PrismicCategory } from './categories/[category]';

const IntercomWidget = dynamic<IntercomWidgetProps>(
  () =>
    import('src/components/intercom-widget/intercom-widget').then(
      (module) => module.IntercomWidget,
    ),
  { ssr: false },
);

// Escapes characters that could break out of a <script> tag when embedding
// JSON-LD via dangerouslySetInnerHTML. JSON.stringify does NOT escape these,
// so a stored "</script>" would otherwise close the tag early (stored XSS).
function escapeJsonLd(json: string) {
  return json
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export type BlogPost = {
  first_publication_date: string;
  uid: string;
  tags: string[];
  data: {
    meta_title: string;
    meta_description: string;
    blog_title: string;
    // Spelled out rather than `Record<string, string>`: an index signature made
    // both of the fields this page actually reads `string | undefined` under
    // `noUncheckedIndexedAccess`, even though every read below assumes them.
    cover_image?: { url?: string | null; alt?: string | null } | null;
    body: {
      slice_type: SliceType;
      primary: {
        text: {
          type: string;
          text: string;
        }[];
      };
    }[];
    author: string;
    categories: { category: string }[];
    schema_jsonld?: string;
    hide_sidebar: boolean;
    sidebar_cta: string;
    sort_by_date: string;
    sidebar_title: string;
    sidebar_primary_cta_text: string;
    sidebar_primary_cta_href: string;
    sidebar_secondary_cta_text: string;
    sidebar_secondary_cta_href: string;
  };
  nextAndPrevUids: string[];
};

export type AuthorUser = {
  uid: string;
  firstName: string;
  lastName: string;
  aboutMe: string;
  title: string;
  profileImageUrl: string;
};

type Props = {
  blogPost: BlogPost;
  // The Prismic document shape, not the narrower `BlogPost` above:
  // `fetchBlogPostsList` returns whole documents, and `BlogCard` — which these
  // are handed to — is typed against them.
  relatedArticles: BlogPostDocument[];
  nextAndPrevUids: string[];
  authorUser: AuthorUser;
};

const BlogPostPage: NextPage<Props> = ({
  blogPost,
  relatedArticles,
  nextAndPrevUids,
  authorUser,
}) => {
  const router = useRouter();

  const [isStickyBarVisible, setIsStickyBarVisible] = useState(false);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const isOnScreen = useOnScreen(ctaRef);
  const previousIsOnScreen = useRef(isOnScreen);
  const AnimatedStickyNavbar = animated(StickyNavbar);
  const transitions = useTransition(isStickyBarVisible, {
    from: { y: -100 },
    enter: { y: 0 },
    leave: { y: -100 },
  });

  useEffect(() => {
    if (isOnScreen && !previousIsOnScreen.current) {
      setIsStickyBarVisible(false);
    } else if (!isOnScreen && previousIsOnScreen.current) {
      const rect = ctaRef.current?.getBoundingClientRect();

      // Comparing against a missing rect yielded `false` (any `>` against
      // `undefined` does), so the explicit check keeps the bar hidden exactly
      // as before.
      setIsStickyBarVisible(rect !== undefined && window.scrollY > rect.top);
    }
    previousIsOnScreen.current = isOnScreen;
  }, [isOnScreen]);

  if (router.isFallback) {
    return (
      <div className="py-5xl relative w-full">
        <Spinner />
      </div>
    );
  }

  if (!blogPost) return null;

  const {
    meta_description: metaDescription,
    blog_title: blogTitle,
    cover_image: coverImage,
    body: slices,
    categories = [],
    schema_jsonld,
    hide_sidebar: hideSidebar,
    sort_by_date: sortByDate,
    sidebar_title: sidebarTitle,
    sidebar_primary_cta_text: sidebarPrimaryCtaText,
    sidebar_primary_cta_href,
    sidebar_secondary_cta_text: sidebarSecondaryCtaText,
    sidebar_secondary_cta_href: sidebarSecondaryCtaHref,
  } = blogPost.data;

  const sidebarPrimaryCtaHref =
    sidebarPrimaryCtaText?.toLowerCase() ===
    HOMEOWNERS_STRINGS['doTheMath.cta'].toLowerCase()
      ? PATHS.RENT_ESTIMATE_FLOW
      : sidebar_primary_cta_href;

  const tags = categories.map(
    (category: { category: string }) => category?.category || '',
  );
  const date = formatDateTime({ dateTime: sortByDate, format: 'MMM d, yyyy' });

  const postLink = `belonghome.com${PATHS.BLOG}/${blogPost.uid}`;
  const shareLinks = [
    { url: `https://www.facebook.com/sharer.php?u=${postLink}`, Icon: IconFb },
    {
      url: `https://www.twitter.com/intent/tweet?url=${postLink}`,
      Icon: IconTwitter,
    },
    {
      url: `https://www.linkedin.com/shareArticle?url=${postLink}`,
      Icon: IconLinkedin,
    },
  ];

  const coverImageUrl = coverImage?.url ?? undefined;
  const coverImageAlt = coverImage?.alt ?? '';

  const BLOG_STRUCTURED_DATA = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blogTitle,
    ...(coverImageUrl ? { image: [coverImageUrl] } : {}),
    datePublished: blogPost.first_publication_date,
    dateModified: sortByDate,
    author: [
      {
        '@type': 'Person',
        name: `${authorUser.firstName} ${authorUser.lastName}`,
        url: `https://belonghome.com${PATHS.ABOUT_AUTHOR.replace(':authorId', authorUser.uid)}`,
      },
    ],
  };

  let tableOfContents: ContentTableItem[] | undefined;
  const allTextContent = slices
    .filter((s) => s.slice_type === 'text' && s.primary.text)
    .flatMap((s) => s.primary.text);
  if (allTextContent.length) {
    tableOfContents = filterHeadingsAndDescendants(allTextContent);
  }

  // Round-trip through JSON to reject anything that isn't valid JSON-LD before
  // injecting it, then escape <script>-breaking characters.
  let safeSchemaJsonLd;
  if (schema_jsonld) {
    try {
      safeSchemaJsonLd = escapeJsonLd(
        JSON.stringify(JSON.parse(schema_jsonld)),
      );
    } catch {
      safeSchemaJsonLd = undefined;
    }
  }

  return (
    <PageViewTracker name="blog_post">
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: escapeJsonLd(JSON.stringify(BLOG_STRUCTURED_DATA)),
          }}
        />
        {safeSchemaJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: safeSchemaJsonLd }}
          />
        )}
      </Head>
      <MetaSEO
        titleTemplate={'%s'}
        title={blogTitle}
        description={metaDescription}
        openGraph={{
          title: blogTitle,
          description: metaDescription,
          ...(coverImageUrl
            ? {
                images: [
                  {
                    url: coverImageUrl,
                    width: 800,
                    height: 500,
                    alt: coverImageAlt,
                    type: 'image/jpeg',
                  },
                ],
              }
            : {}),
        }}
      />
      <IntercomWidget isEnabled />
      <div className="mb-xl flex w-full flex-col items-center">
        <div className="w-full overflow-hidden" /* hide cover overflow */>
          <div className="pt-lg md:pt-3xl container flex flex-col">
            <div className="mb-xs">
              {!!tags?.length && <BlogTags tags={tags} showAllTags />}
            </div>
            <div className="mb-xl">
              <Text variant="h2" as="h1" fontWeight="semibold">
                {blogTitle}
              </Text>
            </div>
            <LinkBase href={`/blog/author/${authorUser.uid}`}>
              <AboutAuthor
                // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
                // `authorUser` is the camelCased projection built in
                // `getStaticProps`, so `AboutAuthor` — which reads
                // `profile_image_url` and `first_name` — finds neither and has
                // never rendered the image `showImg` asks for. Suppressed rather
                // than mapped, because mapping would turn those images on.
                author={authorUser}
                showImg
                firstLine={
                  <>
                    <Text as="span">{BLOG_STRINGS['card.writtenBy']}</Text>
                    <Text
                      as="span"
                      fontWeight="semibold"
                    >{` ${authorUser.firstName} ${
                      authorUser.lastName ? authorUser.lastName : ''
                    }`}</Text>
                  </>
                }
                secondLine={
                  <Text>
                    {BLOG_STRINGS['post.lastUpdated']} {date}
                  </Text>
                }
              />
            </LinkBase>
          </div>
          <div className="contents lg:container lg:block">
            <div className={clsx('mb-xl mt-3xl w-full', css.coverContainer)}>
              <div className="relative w-full">
                <div className={css.neonSignContainer}>
                  <div className="aspect-h-5 aspect-w-13">
                    <Image src="/blog/belong_blog_neon_sign.png" alt="" />
                  </div>
                </div>
              </div>
              {coverImageUrl ? (
                <PrismicImageAdapter
                  src={coverImageUrl}
                  alt={coverImageAlt}
                  sizes="(min-width: 1200px) 1170px,
                100vw"
                  className="object-cover lg:rounded"
                  preload
                  Head={Head}
                  config={{ auto: 'format,compress' }}
                />
              ) : null}
            </div>
          </div>
        </div>
        <div className="container">
          <div className="flex w-full flex-col items-center xl:flex-row-reverse xl:items-stretch">
            {!hideSidebar && (
              <div className="mb-xl xl:ml-lg xl:pt-sm xl:mb-0">
                {!!tableOfContents?.length && (
                  <div className="mb-xl hidden xl:block">
                    <ContentTable tableOfContents={tableOfContents} />
                  </div>
                )}
                <div className="xl:top-2xl xl:sticky">
                  <HomeownersWidget
                    title={sidebarTitle}
                    primaryCta={sidebarPrimaryCtaText}
                    primaryHref={sidebarPrimaryCtaHref}
                    secondaryCta={sidebarSecondaryCtaText}
                    secondaryHref={sidebarSecondaryCtaHref}
                    ctaRef={ctaRef}
                  />
                </div>
              </div>
            )}
            <div
              className={clsx(
                hideSidebar ? css.bodyNoCol : '',
                'flex min-w-0 max-w-full flex-1 grow flex-col',
              )}
            >
              <div className="mb-2xl flex items-center">
                <div className="mr-2sm last:mr-0">
                  <Text>{BLOG_STRINGS['post.share']}</Text>
                </div>
                {shareLinks.map(({ url, Icon }, idx) => (
                  <LinkBase
                    href={url}
                    className="mr-2sm last:mr-0"
                    target="_blank"
                    rel="noreferrer"
                    key={idx}
                  >
                    {<Icon height={16} />}
                  </LinkBase>
                ))}
              </div>
              <div className="flex flex-1 flex-col items-center">
                {slices.map((slice, idx) => {
                  const { slice_type, ...sliceProps } = slice;
                  const SliceComponent = SLICE_COMPONENT_MAP[slice_type];
                  if (!SliceComponent) return null;

                  return <SliceComponent {...sliceProps} key={idx} />;
                })}
              </div>
              {categories.some(
                (category) => category.category === 'New & Improved',
              ) && <RequestFeature />}
              {authorUser.aboutMe && (
                <>
                  <div className="my-2xl border-gray border-0 border-t border-solid" />
                  <div className="mb-md">
                    <Text variant="h3" fontWeight="semibold">
                      {BLOG_STRINGS['post.aboutAuthor']}
                    </Text>
                  </div>
                  <LinkBase href={`/blog/author/${authorUser.uid}`}>
                    <>
                      <AboutAuthor
                        // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
                        // Same projection mismatch as the header instance above.
                        author={authorUser}
                        showImg
                        firstLine={
                          <Text fontWeight="semibold">{`${authorUser.firstName} ${authorUser.lastName}`}</Text>
                        }
                        secondLine={<Text>{authorUser.title}</Text>}
                      />
                      <Text className="mt-sm">{authorUser.aboutMe}</Text>
                    </>
                  </LinkBase>
                </>
              )}
              <div className="mt-xl flex w-full flex-row-reverse justify-between">
                {nextAndPrevUids.map((uid, idx) => (
                  <Fragment key={idx}>
                    {uid ? (
                      <Link href={uid}>
                        <div className="flex items-center">
                          {nextAndPrevButtonsContent[idx]}
                        </div>
                      </Link>
                    ) : (
                      <div className="text-gray flex items-center font-semibold uppercase">
                        {nextAndPrevButtonsContent[idx]}
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
          {!!relatedArticles?.length && (
            <div className="mt-xl">
              <div className="mb-md mt-4xl">
                <Text variant="h3" fontWeight="semibold">
                  {BLOG_STRINGS['relatedArticles']}
                </Text>
              </div>
              <div className="mb-3xl gap-x-lg gap-y-2xl md:gap-y-3xl grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                {relatedArticles?.map((article) => (
                  <BlogCard
                    variant="page-reduced"
                    post={article}
                    key={article.uid}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="relative block w-full xl:hidden">
          {transitions(
            (styles, isStickyBarVisible) =>
              isStickyBarVisible && (
                <AnimatedStickyNavbar
                  style={styles}
                  cta={sidebarPrimaryCtaText}
                  href={sidebarPrimaryCtaHref}
                  // `?? []` because a post with no headings has no table of
                  // contents; `StickyNavbar` already renders nothing for an
                  // empty list, which is what it did for `undefined` too.
                  tableOfContents={tableOfContents ?? []}
                />
              ),
          )}
        </div>
      </div>
    </PageViewTracker>
  );
};

// TODO: Couldn't get the prismic webhooks working, will bring back once they are fixed.
// export async function getStaticPaths() {
//   const client = createClient();
//   const response = await fetchAllBlogPosts(client);

//   const paths = response.map((blogPost) => {
//     return { params: { uid: blogPost.uid } };
//   });

//   return {
//     paths,
//     fallback: false,
//   };
// }

export const getServerSideProps: GetServerSideProps = async ({
  query,
  previewData,
}) => {
  const defaultProps = {
    props: { blogPost: null, relatedArticles: [], nextAndPrevUids: [] },
  };
  try {
    const client = createClient({ previewData });
    if (typeof query?.uid !== 'string') return defaultProps;

    const blogPost = await fetchBlogPostByUid(query.uid, client).catch(
      () => null,
    );
    if (!blogPost) {
      return {
        notFound: true,
      };
    }

    if (query.uid !== blogPost?.uid) {
      return {
        redirect: {
          permanent: true,
          destination: `${PATHS.BLOG}/${blogPost?.uid}`,
        },
      };
    }

    const { results: relatedArticles } = await fetchBlogPostsList({
      categories: blogPost.data.categories?.map(
        ({ category }: PrismicCategory) => category || '',
      ),
      page: 1,
      client,
      pageProps: { pageSize: 3 },
      excludedUIDs: [blogPost.uid!],
    });
    const nextAndPrev = await Promise.all([
      fetchNextBlogPost(blogPost.id, client),
      fetchPreviousBlogPost(blogPost.id, client),
    ]);

    const nextAndPrevUids = nextAndPrev.map((res) => res.results[0]?.uid || '');

    const blogProfileMatch = await fetchBlogProfileByUid(
      blogPost?.data?.author,
      client,
    );

    const authorUser = {
      firstName: blogProfileMatch?.data?.first_name,
      lastName: blogProfileMatch?.data?.last_name || null,
      aboutMe: blogProfileMatch?.data?.about_me || null,
      title: blogProfileMatch?.data?.title || null,
      profileImageUrl: blogProfileMatch?.data?.profile_image_url?.url || null,
      uid: blogProfileMatch?.uid,
    };

    return {
      props: { blogPost, relatedArticles, nextAndPrevUids, authorUser },
    };
  } catch {
    return defaultProps;
  }
};

export default BlogPostPage;
